// Sentry init for the admin panel SPA.
//
// We initialise once at app entry. DSN is read from `VITE_SENTRY_DSN`
// because Vite only exposes vars that start with `VITE_*` to the
// client bundle, and DSNs are public credentials by design.
//
// Init is a no-op when DSN is unset so dev environments don't need a
// reporter to come up.

import * as Sentry from '@sentry/react';

export function initSentry() {
  const dsn = (import.meta.env.VITE_SENTRY_DSN ?? '').trim();
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENV ?? import.meta.env.MODE,
    // Vite injects the commit SHA via VITE_RELEASE in CI builds.
    // Locally we fall back to `dev` so events still group sanely.
    release: import.meta.env.VITE_RELEASE ?? 'dev',

    // Conservative perf sampling. The admin panel is a low-traffic
    // internal tool; 25% is enough to spot slow flows without
    // bloating the dashboard.
    tracesSampleRate: 0.25,

    // Capture Replay only on errors — same reasoning as the website
    // (heavyweight on every page view, surgical on incidents).
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // The panel displays reader comments, user emails, and audit
        // IPs — all of it must be masked before it leaves the browser.
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],

    // Silence noisy environments without losing real signal.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
    ],
  });
}

// Re-export so call-sites don't import @sentry/react directly. Keeping
// the surface small lets us swap the underlying reporter later without
// a project-wide rename.
export { Sentry };
